import React, { useState, useEffect } from 'react';

const LoadingScreen = ({ isVisible, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const loadingSteps = [
    { text: "Connecting to Google...", duration: 800 },
    { text: "Verifying credentials...", duration: 1000 },
    { text: "Checking permissions...", duration: 700 },
    { text: "Setting up your session...", duration: 600 },
    { text: "Almost ready...", duration: 500 }
  ];

  useEffect(() => {
    if (!isVisible) {
      setCurrentStep(0);
      return;
    }

    let stepTimer;
    let totalDuration = 0;

    const runSteps = () => {
      loadingSteps.forEach((step, index) => {
        setTimeout(() => {
          setCurrentStep(index);
        }, totalDuration);
        totalDuration += step.duration;
      });

      // Complete after all steps
      setTimeout(() => {
        setTimeout(() => {
          onComplete?.();
        }, 300);
      }, totalDuration);
    };

    runSteps();

    return () => {
      clearTimeout(stepTimer);
    };
  }, [isVisible, onComplete]);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-white flex items-center justify-center z-50">
      {/* Animated background particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-blue-500 rounded-full opacity-20 animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 2}s`,
              animationDuration: `${2 + Math.random() * 3}s`
            }}
          />
        ))}
      </div>

      <div className="text-center z-10 max-w-md mx-auto px-6">
        {/* Main logo/icon */}
        <div className="mb-8">
          <div className="w-20 h-20 mx-auto mb-4 relative">
            <div className="absolute inset-0 bg-blue-500 rounded-full opacity-20 animate-ping"></div>
            <div className="absolute inset-2 bg-blue-500 rounded-full opacity-40 animate-ping animation-delay-75"></div>
            <div className="absolute inset-4 bg-blue-500 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          </div>
        </div>

        {/* Loading text */}
        <h2 className="text-2xl font-bold text-gray-800 mb-2 animate-pulse">
          Signing You In
        </h2>
        
        {/* Current step */}
        <p className="text-gray-600 mb-8 h-6 transition-all duration-300 ease-in-out">
          {loadingSteps[currentStep]?.text}
        </p>

        {/* Progress bar */}
        <div className="w-full bg-gray-200 rounded-full h-2 mb-4 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 h-full rounded-full relative">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 w-1/3 animate-pulse"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-blue-500 animate-pulse"></div>
          </div>
        </div>

        {/* Step indicators */}
        <div className="flex justify-center space-x-2 mt-6">
          {loadingSteps.map((_, index) => (
            <div
              key={index}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index <= currentStep 
                  ? 'bg-blue-500 shadow-lg' 
                  : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default LoadingScreen;